"use client";

import { BadgeCheck, ChevronRight, FlaskConical, Heart, LifeBuoy, LogOut, Palette, Shield, Terminal, User } from "lucide-react";
import Link from "next/link";
import { SidebarLink } from "./SidebarLink";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarSeparator } from "@src/components/ui/sidebar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from "@src/components/ui/dropdown-menu";
import { paths } from "@src/utils/paths";
import { useAuth } from "./providers/auth-provider";
import { useDevToolsOptional } from "@lib/audio/devtools";
import { useShowDevTools } from "@src/hooks/audio/useShowDevTools";
import { useColorTheme } from "./providers/color-theme-provider";
import { useIsMobile } from "@src/hooks/use-mobile";
export function NavFooter() {
  const {
    user,
    signOut,
    isSuperuser
  } = useAuth();
  const devTools = useDevToolsOptional();
  const showDevToolsPermission = useShowDevTools();
  const {
    colorTheme,
    setColorTheme,
    showSelector
  } = useColorTheme();
  const isMobile = useIsMobile();
  const showDevTools = devTools && showDevToolsPermission;
  const showAdminSection = isSuperuser || showDevTools;
  if (!user) return null;
  return <SidebarMenu data-sentry-element="SidebarMenu" data-sentry-component="NavFooter" data-sentry-source-file="NavFooter.tsx">
      {/* Admin/Dev section */}
      {isSuperuser && <SidebarMenuItem>
          <SidebarMenuButton asChild>
            <SidebarLink href={paths.admin} data-ph-capture-attribute-action="nav-admin">
              <Shield className="h-4 w-4" />
              <span>Admin</span>
            </SidebarLink>
          </SidebarMenuButton>
        </SidebarMenuItem>}
      {showDevTools && <SidebarMenuItem>
          <SidebarMenuButton onClick={devTools.toggle} data-ph-capture-attribute-action="nav-devtools">
            <Terminal className="h-4 w-4" />
            <span>AudioEngine DevTools</span>
          </SidebarMenuButton>
        </SidebarMenuItem>}
      {showAdminSection && <SidebarSeparator className="mx-0" />}

      {/* Main footer items */}
      {/* TODO: Unhide "What's new?" link once changelog content is ready */}

      {showSelector && <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton data-ph-capture-attribute-action="nav-theme">
                <Palette className="h-4 w-4" />
                <span>Theme</span>
                <ChevronRight className="ml-auto h-4 w-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent side={isMobile ? "top" : "right"} align={"end"}>
              <DropdownMenuRadioGroup value={colorTheme} onValueChange={v => setColorTheme(v as "warm" | "classic")}>
                <DropdownMenuRadioItem value="classic">Classic</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="warm">Warm</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>}

      <SidebarMenuItem data-sentry-element="SidebarMenuItem" data-sentry-source-file="NavFooter.tsx">
        <SidebarMenuButton asChild data-sentry-element="SidebarMenuButton" data-sentry-source-file="NavFooter.tsx">
          <SidebarLink href={paths.support} data-ph-capture-attribute-action="nav-support" data-sentry-element="SidebarLink" data-sentry-source-file="NavFooter.tsx">
            <LifeBuoy className="h-4 w-4" data-sentry-element="LifeBuoy" data-sentry-source-file="NavFooter.tsx" />
            <span>Support</span>
          </SidebarLink>
        </SidebarMenuButton>
      </SidebarMenuItem>

      <SidebarMenuItem data-sentry-element="SidebarMenuItem" data-sentry-source-file="NavFooter.tsx">
        <SidebarMenuButton asChild data-sentry-element="SidebarMenuButton" data-sentry-source-file="NavFooter.tsx">
          <Link href={paths.donate} target="_blank" data-ph-capture-attribute-action="nav-donate" data-sentry-element="Link" data-sentry-source-file="NavFooter.tsx">
            <Heart className="h-4 w-4" data-sentry-element="Heart" data-sentry-source-file="NavFooter.tsx" />
            <span>Donate</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>

      <SidebarSeparator className="mx-0" data-sentry-element="SidebarSeparator" data-sentry-source-file="NavFooter.tsx" />

      {/* User submenu */}
      <SidebarMenuItem data-sentry-element="SidebarMenuItem" data-sentry-source-file="NavFooter.tsx">
        <DropdownMenu data-sentry-element="DropdownMenu" data-sentry-source-file="NavFooter.tsx">
          <DropdownMenuTrigger asChild data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="NavFooter.tsx">
            <SidebarMenuButton data-sentry-element="SidebarMenuButton" data-sentry-source-file="NavFooter.tsx">
              <User className="h-4 w-4" data-sentry-element="User" data-sentry-source-file="NavFooter.tsx" />
              <span>{user.user_metadata["name"]}</span>
              <ChevronRight className="ml-auto h-4 w-4" data-sentry-element="ChevronRight" data-sentry-source-file="NavFooter.tsx" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent side={isMobile ? "top" : "right"} align={"end"} data-sentry-element="DropdownMenuContent" data-sentry-source-file="NavFooter.tsx">
            <DropdownMenuItem asChild data-ph-capture-attribute-action="nav-account" data-sentry-element="DropdownMenuItem" data-sentry-source-file="NavFooter.tsx">
              <SidebarLink href={paths.account} data-sentry-element="SidebarLink" data-sentry-source-file="NavFooter.tsx">
                <BadgeCheck className="h-4 w-4" data-sentry-element="BadgeCheck" data-sentry-source-file="NavFooter.tsx" />
                Account
              </SidebarLink>
            </DropdownMenuItem>
            <DropdownMenuItem asChild data-ph-capture-attribute-action="nav-early-access" data-sentry-element="DropdownMenuItem" data-sentry-source-file="NavFooter.tsx">
              <SidebarLink href={paths.earlyAccess} data-sentry-element="SidebarLink" data-sentry-source-file="NavFooter.tsx">
                <FlaskConical className="h-4 w-4" data-sentry-element="FlaskConical" data-sentry-source-file="NavFooter.tsx" />
                Early access
              </SidebarLink>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => signOut()} data-ph-capture-attribute-action="nav-sign-out" data-sentry-element="DropdownMenuItem" data-sentry-source-file="NavFooter.tsx">
              <LogOut className="h-4 w-4" data-sentry-element="LogOut" data-sentry-source-file="NavFooter.tsx" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>;
}